import { useState, useEffect, useRef } from 'react'
import { Container, Col, Row, Form } from 'react-bootstrap'
import Tabs from 'react-bootstrap/Tabs'
import Spinner from "react-bootstrap/Spinner"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faFloppyDisk, 
  faPen, 
  faChevronLeft
} from "@fortawesome/free-solid-svg-icons"

import COLORS from '../../../../../data/colors'
import useBreakpoint from '../../../../../hooks/useBreakpoint'
import useUser from '../../../../../hooks/useUser'

import { useLazyQuery, useMutation } from '@apollo/client/react'
import { useParams, useNavigate, Link } from 'react-router-dom'

import GET_PROGRAMME from '../../../../../graphql/queries/courseCatalogue/getProgramme'
import GET_PROGRAMME_FORM from "../../../../../graphql/queries/courseCatalogue/getProgrammeForm"

import EDIT_PROGRAMME from '../../../../../graphql/mutations/courseCatalogue/editProgramme'

import CustomTab from '../../../../../components/CustomTab'
import CustomModal from '../../../../../components/CustomModal'
import CustomButton from '../../../../../components/CustomButton'

import toastConfiguration from "../../../../../data/toastConfiguration"
import { ToastContainer, toast } from "react-toastify"

import { useTranslation } from 'react-i18next'

import DynamicForm from '../../../../../components/DynamicForm'

import { 
  convertProgrammeStringToNumbers, 
  isDeepEqual, 
  getPatchDiff,
  parseEntityHeader,
  parseEntityValue,
  processForm
} from '../../../../../utils/courseCatalogueManager'

const GROUPS = {
  general: [
    "title",
    "code",
    "language_of_instruction",
    "programme__abbreviation",
    "url",
    "description", // textarea
    "programme__valid_since",
    "programme__valid_until"
  ],
  programme: [
    "programme__ects",
    "programme__eqf_level_provided",
    "programme__length",
    "programme__isced_code",
    "learning_outcomes", // textarea
  ],
  elm: [
    "programme__elm_learning_schedule",
    "programme__elm_mode_of_learning",
    "programme__elm_lo_type",
  ],
}

const SingleViewProgramme = () => {
    
  const { t } = useTranslation('translation', {keyPrefix: 'courseCatalogue.programmesSection.singleView'})

  const [size] = useBreakpoint()
  const { programmeID } = useParams()
  const { user } = useUser()
  const navigate = useNavigate()

  const formRef = useRef(null)

  const [programmeData, setProgrammeData] = useState({})
  const [programmeForm, setProgrammeForm] = useState({})
  const [loadingComponent, setLoadingComponent] = useState(true)
  const [fieldOptions, setFieldOptions] = useState({})
  const [validated, setValidated] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [selectedAction, setSelectedAction] = useState("")
  const [formData, setFormData] = useState({})

  const userHasPermission = user?.permissions?.some(
    (item) => item.value === "manageCC"
  )

  const [loadProgramme, { 
    calledGetProgramme, 
    loading: loadingGetProgramme, 
    data: dataGetProgramme, 
    error: errorGetProgramme,
    refetch: refetchProgramme }
  ] = useLazyQuery(GET_PROGRAMME, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      console.log("Programme by ID data received")

      setProgrammeData(data.programme)
    },
    onError: (error) => {
      console.error("Error loading Programme by ID")
    }
  })

  const [loadGetProgrammeForm, { 
    loading: loadingGetProgrammeForm, dataGetProgrammeForm, errorGetProgrammeForm }
  ] = useLazyQuery(GET_PROGRAMME_FORM, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      console.log("Programme Form data received")

      // Save the form into session storage
      sessionStorage.setItem("programmeForm", JSON.stringify(data.getProgrammeForm))
      setProgrammeForm(data.getProgrammeForm)

      const fieldOptionsObj = processForm(data.getProgrammeForm)
      setFieldOptions(fieldOptionsObj)

    },
    onError: (error) => {
      console.error("Error loading Programme Form")
    }
  })

  const [callEditProgramme, { 
    loading: loadingEditProgramme, dataEditProgramme }
  ] = useMutation(EDIT_PROGRAMME, {
    onCompleted: (data) => {
      console.log("Programme modified")
    },
    onError: (error) => {
      console.error("Error editing Programme")
    }
  })

  const handleFormChange = (data) => {
    setFormData(data)
  }

  const formIsValidated = (e) => {
    setValidated(true)
    e.preventDefault()

    if (!formRef.current.checkValidity()) {
      e.stopPropagation()
      return false
    }

    return true
  }

  const handleEdit = async (e) => {

    if (!formIsValidated(e)) return

    const toastId = toast.loading(t('editProgramme.loading'), toastConfiguration)

    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))
    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey

    let cleanedData = convertProgrammeStringToNumbers(formData)
    cleanedData.id = programmeData.id
    cleanedData.uuid = programmeData.uuid
    cleanedData.status = programmeData.status

    // Check if the data has changed
    // If yes, run the query
    // if not, show an error message
    if(!isDeepEqual(programmeData, cleanedData)) {
      try {
        let result = {}

        result = await callEditProgramme({
          variables: {
            programmeData: getPatchDiff(programmeData, cleanedData),
            baseUrl,
            apiKey
          }
        })

        setValidated(false)
        
        if (!result?.errors) {

          toast.update(toastId, {
            ...toastConfiguration,
            render: `${t('editProgramme.success.part1')} ${cleanedData.title[0].string} ${t('editProgramme.success.part2')}`,
            type: "success",
            isLoading: false,
          })
          
          const { data: refreshedProgramme } = await refetchProgramme({
            variables: { baseUrl, apiKey }
          })

          setProgrammeData(refreshedProgramme.programme)

          setShowModal(false)

        } else {

          console.error("Edit Programme failed.")

          toast.update(toastId, {
            ...toastConfiguration,
            render: t('editProgramme.failure1') + result?.errors,
            type: "error",
            isLoading: false,
          })
        }

      } catch (err) {
        
        console.error("Edit Programme failed:", err)

        toast.update(toastId, {
          ...toastConfiguration,
          render: t('editProgramme.failure2'),
          type: "error",
          isLoading: false,
        })
      }

    } else {
      toast.update(toastId, {
        ...toastConfiguration,
        render: t('editProgramme.noChanges'),
        type: "error",
        isLoading: false,
      })
    }

  }

  // Get Programme
  useEffect(() => {

    const heiConnectionStorage = JSON.parse(localStorage.getItem('heiConnection'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))

    // If userId and backend username don't match, redirects to /cc
    if(user?.userId !== userConnectionStorage?.backendUserName) {
      navigate('/cc')
      return
    }

    // If there are no connections redirects to /cc
    if(!heiConnectionStorage || !userConnectionStorage) {
      navigate('/cc')
      return
    }

    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey

    const programmeForm = JSON.parse(sessionStorage.getItem('programmeForm'))

    if(!programmeForm){
      loadGetProgrammeForm({
        variables: { baseUrl, apiKey }
      })
    } else {
      
      // Save the fields form schema with type select in a separate object
      const fieldOptionsObj = processForm(programmeForm)

      setProgrammeForm(programmeForm)
      setFieldOptions(fieldOptionsObj)
      /* setLoadingComponent(false) */
    }

    if(userHasPermission
      && (heiConnectionStorage 
      || userConnectionStorage)){
      loadProgramme({
        variables: {
          programmeID,
          baseUrl
        }
      })
      setLoadingComponent(false)
    } else {
      navigate('/cc')
      return
    }

  }, [loadProgramme])

  if (errorGetProgramme?.message === "404")
    return (
      <Container>
        <Row className="mt-5 pt-5 d-flex justify-content-between">
          <p className="text-center text-danger mt-5 mb-4">{'We couldn’t find this programme.'}</p>
          <p className="text-center fst-italic mb-4">{'It may have been removed or the link might be incorrect.'}</p>
          <p className="text-center">
            <Link to="/cc/programmes">{'Back to list'}</Link>
          </p>
        </Row>
      </Container>
    )

  if (errorGetProgramme)
    return (
      <div className="text-center text-danger mx-auto mt-5 pt-5">{t('errorLoadingProgramme')}</div>
    )

  if (loadingGetProgramme || loadingComponent) {
    return (
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)"
        }}
      >
        <Spinner
          as="div"
          animation="grow"
          size="sm"
          role="status"
          aria-hidden="true"
          className="align-middle my-auto me-2"
        />
        <span>{t('loading')}</span>
      </div>
    )
  }

  return (
    <>
      <Container>
        <Row className="mt-5 pt-5 d-flex justify-content-between">
          <Col md={2}>
            <Link to="/cc/programmes" className="goBackLink">
              <FontAwesomeIcon
                icon={faChevronLeft}
                color={COLORS.greyText}
                className="with-icon me-1"
                id={"smallIcon"}
              />
              {t('goBack')}
            </Link>
          </Col>
          <Col md={3} className="d-flex justify-content-end">
            <CustomButton type="submit" buttonType={"primary"} icon={faPen} text={t('editButton')} handleClick={() => {
              setSelectedAction("edit")
              setValidated(false)
              setShowModal(true)
            }} hasIcon styles={{ marginLeft: 20 }} small />
          </Col>
        </Row>
      </Container>

      <Container>
        <div className="mt-4 pt-4">
          <Tabs id="general">
            <CustomTab eventKey="general" title={t('general.title')} style={{ background: "#fff" }}>
              <Row className="px-4 py-3">
                {GROUPS.general.map((key, idx) => {
                  const value = programmeData[key]

                  return (
                    <Col md={4} key={`col-${idx}`} className="my-4">
                      <div className="label-style fw-bold text-uppercase mb-1">
                        {parseEntityHeader(key)}
                      </div>
                      <div className="body">
                        {Array.isArray(value) && value.length > 0 ? (
                          value.map((entry, i) => {
                            const text = entry?.string || entry?.multiline || entry?.uri || entry
                            const lang = entry?.lang || ""
                            return (
                              <div key={`entry-${idx}-${i}`} className="my-2">
                                {parseEntityValue(key, text, fieldOptions)}
                                {lang.length !== 0 && 
                                  <span className="ms-2">
                                    ({parseEntityValue(key, lang, fieldOptions)})
                                  </span>
                                }
                              </div>
                            )
                          })
                        ) : (
                          parseEntityValue(key, value, fieldOptions)
                        )}
                      </div>
                    </Col>
                  )
                })}
              </Row>
            </CustomTab>
          </Tabs>
        </div>

        <div className="mt-4 pt-4">
          <Tabs id="programme">
            <CustomTab eventKey="programme" title={t('programme.title')} style={{ background: "#fff" }}>
              <Row className="px-4 py-3">
                {GROUPS.programme.map((key, idx) => {
                  const value = programmeData[key]

                  return (
                    <Col md={4} key={`col-${idx}`} className="my-4">
                      <div className="label-style fw-bold text-uppercase mb-1">
                        {parseEntityHeader(key)}
                      </div>
                      <div className="body">
                        {Array.isArray(value) && value.length > 0 ? (
                          value.map((entry, i) => {
                            
                            const text = entry?.string || entry?.multiline || entry?.uri || entry
                            const lang = entry?.lang || ""
                            return (
                              <div key={`entry-${idx}-${i}`} className="my-2">
                                {parseEntityValue(key, text, fieldOptions)}
                                {lang.length !== 0 && 
                                  <span className="ms-2">
                                    ({parseEntityValue(key, lang, fieldOptions)})
                                  </span>
                                }
                              </div>
                            )
                          })
                        ) : (
                          parseEntityValue(key, value, fieldOptions)
                        )}
                      </div>
                    </Col>
                  )
                })}
              </Row>
            </CustomTab>
          </Tabs>
        </div>

        <div className="mt-4 pt-4">
          <Tabs id="elm">
            <CustomTab eventKey="elm" title="ELM" style={{ background: "#fff" }}>
              <Row className="px-4 py-3">
                {GROUPS.elm.map((key, idx) => {
                  const value = programmeData[key]

                  return (
                    <Col md={4} key={`col-${idx}`} className="my-4">
                      <div className="label-style fw-bold text-uppercase mb-1">
                        {parseEntityHeader(key)}
                      </div>
                      <div className="body">
                        {Array.isArray(value) && value.length > 0 ? (
                          value.map((entry, i) => {
                            const text = entry?.string || entry?.multiline || entry?.uri || entry
                            const lang = entry?.lang || ""
                            return (
                              <div key={`entry-${idx}-${i}`} className="my-2">
                                {parseEntityValue(key, text, fieldOptions)}
                                {lang.length !== 0 && 
                                  <span className="ms-2">
                                    ({parseEntityValue(key, lang, fieldOptions)})
                                  </span>
                                }
                              </div>
                            )
                          })
                        ) : (
                          parseEntityValue(key, value, fieldOptions)
                        )}
                      </div>
                    </Col>
                  )
                })}
              </Row>
            </CustomTab>
          </Tabs>
        </div>

        <CustomModal
          show={showModal}
          handleClose={() => {
            setShowModal(false)
            setValidated(false)
          }}
          disabledCancelButton={
            loadingEditProgramme
          }
          body={
            (selectedAction === "edit") && (
              <div>
                
                <Form ref={formRef} noValidate validated={validated}>
                  <DynamicForm 
                    schema={programmeForm}
                    onChange={handleFormChange}
                    defaultValues={selectedAction === 'edit' ? programmeData : {}}
                  />
                </Form>
                
              </div>
            )
          }
          button={
            selectedAction === "edit" && ( 
              <CustomButton
                buttonType={"primary"}
                disabled={loadingEditProgramme}
                text={t('saveChanges')}
                icon={faFloppyDisk}
                hasIcon={true}
                iconColor={COLORS.white}
                handleClick={handleEdit}
              />
            )
          }
          title={
            selectedAction === "edit" && (
              t('edit.title')
            )
          }
          subtitle={
            selectedAction === "edit" && (
              t('edit.subtitle')
            )
          }
          hasIcon
          fullscreen
        />
      </Container>

      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}

export default SingleViewProgramme